import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Player, GameRole } from '../../types';
import { dictionaries } from '../../utils/dictionary';
import {
  SectionCard,
  InstructionCard,
  SuccessCard,
  PhaseButton,
  PlayerAvatar,
  MetaInfo,
  MetaHighlight,
} from '../../styles/SharedStyles';
import { theme } from '../../styles/theme';

interface MorningTransitionProps {
  language: number;
  players: Player[];
  playerRoles: GameRole[];
  playerFauls: number[];
  eliminatedPlayer?: number;
  nightCount: number;
  isFirstNight: boolean;
  firstDayEliminations: number;
  onBestMove: (guesses: number[]) => void;
  onLastWords: () => void;
  onContinue: () => void;
  onUndo?: () => void;
}

const TransitionCard = styled(SectionCard)<{ $variant?: 'info' | 'primary' | 'warning' }>`
  background: ${({ $variant }) => {
    if ($variant === 'primary')
      return 'linear-gradient(145deg, rgba(22, 14, 42, 0.96), rgba(12, 8, 28, 0.96))';
    if ($variant === 'warning')
      return 'linear-gradient(145deg, rgba(40, 32, 18, 0.96), rgba(28, 22, 12, 0.96))';
    return 'linear-gradient(145deg, rgba(18, 28, 40, 0.96), rgba(12, 20, 32, 0.96))';
  }};
  border: 1px solid
    ${({ $variant }) => {
      if ($variant === 'primary') return 'rgba(124, 93, 255, 0.35)';
      if ($variant === 'warning') return 'rgba(255, 196, 0, 0.35)';
      return 'rgba(0, 186, 255, 0.35)';
    }};
  box-shadow: ${({ $variant }) => {
    if ($variant === 'primary') return '0 20px 60px rgba(124, 93, 255, 0.2)';
    if ($variant === 'warning') return '0 20px 60px rgba(255, 196, 0, 0.15)';
    return '0 20px 60px rgba(0, 186, 255, 0.15)';
  }};
`;

const TransitionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const TransitionTitle = styled.h5`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #dcd4ff;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const CloseButton = styled.button.attrs({ type: 'button' })`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  font-size: 1.25rem;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: rotate(90deg);
  }
`;

const PlayerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};

  @media (min-width: ${theme.breakpoints.mobile}) {
    flex-direction: row;
    align-items: center;
  }
`;

const PlayerCard = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);

  @media (min-width: ${theme.breakpoints.mobile}) {
    min-width: 200px;
  }
`;

const PlayerAvatarLarge = styled(PlayerAvatar)`
  width: 64px;
  height: 64px;
  border-radius: ${theme.borderRadius.lg};
  border: 2px solid rgba(124, 93, 255, 0.5);
`;

const PlayerNameLarge = styled.strong`
  font-size: 1.1rem;
  text-align: center;
`;

const PlayerNote = styled.small`
  font-size: 0.85rem;
  opacity: 0.75;
  text-align: center;
`;

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const BestMoveGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  justify-content: center;
`;

const BestMoveButton = styled.button.attrs({ type: 'button' })<{ $active?: boolean }>`
  min-width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  font-weight: 700;
  font-size: 1rem;
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(124, 93, 255, 0.65)' : 'rgba(255, 255, 255, 0.18)'};
  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(135deg, rgba(124, 93, 255, 0.45), rgba(70, 46, 150, 0.65))'
      : 'rgba(255, 255, 255, 0.05)'};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: all ${theme.transitions.normal};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 12px 32px rgba(124, 93, 255, 0.3);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  justify-content: center;
  margin-top: ${theme.spacing.lg};
`;

const UndoButton = styled(PhaseButton)`
  background: linear-gradient(135deg, rgba(255, 87, 87, 0.25), rgba(200, 40, 40, 0.35));
  border-color: rgba(255, 87, 87, 0.45);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(255, 87, 87, 0.35), rgba(200, 40, 40, 0.45));
  }
`;

const MorningTransition: React.FC<MorningTransitionProps> = ({
  language,
  players,
  playerRoles,
  playerFauls,
  eliminatedPlayer,
  nightCount,
  isFirstNight,
  firstDayEliminations,
  onBestMove,
  onLastWords,
  onContinue,
  onUndo,
}) => {
  const [bestMoveGuesses, setBestMoveGuesses] = useState<number[]>([]);
  const [showBestMove, setShowBestMove] = useState(false);
  const [showLastWords, setShowLastWords] = useState(false);

  const dict = dictionaries[language].ph;

  useEffect(() => {
    if (eliminatedPlayer && isFirstNight) {
      const bestMoveAllowed = firstDayEliminations < 2;

      if (bestMoveAllowed) {
        setShowBestMove(true);
        setShowLastWords(false);
      } else {
        setShowBestMove(false);
        setShowLastWords(true);
      }
    } else if (eliminatedPlayer && !isFirstNight) {
      setShowLastWords(true);
      setShowBestMove(false);
    }
  }, [eliminatedPlayer, isFirstNight, firstDayEliminations]);

  const handleBestMoveToggle = (playerNumber: number) => {
    const newGuesses = bestMoveGuesses.includes(playerNumber)
      ? bestMoveGuesses.filter((p) => p !== playerNumber)
      : [...bestMoveGuesses, playerNumber];

    setBestMoveGuesses(newGuesses);
  };

  const handleBestMoveSubmit = () => {
    onBestMove(bestMoveGuesses);
    setShowBestMove(false);
    if (eliminatedPlayer) {
      setShowLastWords(true);
    } else {
      onContinue();
    }
  };

  const handleBestMoveSkip = () => {
    onBestMove([]);
    setShowBestMove(false);
    if (eliminatedPlayer) {
      setShowLastWords(true);
    } else {
      onContinue();
    }
  };

  const handleLastWordsComplete = () => {
    onLastWords();
    setShowLastWords(false);
    onContinue();
  };

  if (!eliminatedPlayer) {
    return (
      <SuccessCard>
        <TransitionHeader>
          <TransitionTitle>{dict.morning}</TransitionTitle>
          <CloseButton onClick={onContinue}>×</CloseButton>
        </TransitionHeader>
        <MetaInfo style={{ marginBottom: theme.spacing.lg }}>
          {dict.no_night_eliminations}
        </MetaInfo>
        <ActionButtons>
          <PhaseButton $variant="primary" onClick={onContinue}>
            {dict.continue_to_day}
          </PhaseButton>
        </ActionButtons>
      </SuccessCard>
    );
  }

  const eliminatedPlayerData = players[eliminatedPlayer - 1];

  if (showBestMove) {
    return (
      <TransitionCard $variant="primary">
        <TransitionHeader>
          <TransitionTitle>
            {dict.player} {eliminatedPlayer} {dict.vanga}
          </TransitionTitle>
          <CloseButton onClick={handleBestMoveSkip}>×</CloseButton>
        </TransitionHeader>

        <PlayerSection>
          <PlayerCard>
            <PlayerAvatarLarge
              src={eliminatedPlayerData?.avatar}
              alt={eliminatedPlayerData?.nickname}
            />
            <PlayerNameLarge>{eliminatedPlayer}. {eliminatedPlayerData?.nickname}</PlayerNameLarge>
            <PlayerNote>{dict.first_victim_note}</PlayerNote>
          </PlayerCard>

          <ContentSection>
            <MetaInfo style={{  }}>
              {dict.select_mafia_prompt}
            </MetaInfo>

            <BestMoveGrid>
              {Array.from({ length: 10 }, (_, index) => {
                const playerNumber = index + 1;
                const isSelected = bestMoveGuesses.includes(playerNumber);

                return (
                  <BestMoveButton
                    key={playerNumber}
                    $active={isSelected}
                    onClick={() => handleBestMoveToggle(playerNumber)}
                    disabled={!isSelected && bestMoveGuesses.length >= 3}
                  >
                    {playerNumber}
                  </BestMoveButton>
                );
              })}
            </BestMoveGrid>
          </ContentSection>
        </PlayerSection>

        <ActionButtons>
          <PhaseButton
            $variant="primary"
            onClick={handleBestMoveSubmit}
            disabled={bestMoveGuesses.length === 0}
          >
            {dict.rdy}
          </PhaseButton>
          <PhaseButton $variant="secondary" onClick={handleBestMoveSkip}>
            {dict.skip}
          </PhaseButton>
          {onUndo && (
            <UndoButton $variant="secondary" onClick={onUndo}>
              {dict.back_to_night}
            </UndoButton>
          )}
        </ActionButtons>
      </TransitionCard>
    );
  }

  if (showLastWords) {
    return (
      <TransitionCard $variant="info">
        <TransitionHeader>
          <TransitionTitle>
            💬 {dict.player} {eliminatedPlayer} {dict.lms}
          </TransitionTitle>
          <CloseButton onClick={handleLastWordsComplete}>×</CloseButton>
        </TransitionHeader>

        <PlayerSection>
          <PlayerCard>
            <PlayerAvatarLarge
              src={eliminatedPlayerData?.avatar}
              alt={eliminatedPlayerData?.nickname}
            />
            <PlayerNameLarge>{eliminatedPlayer}. {eliminatedPlayerData?.nickname}</PlayerNameLarge>
            <PlayerNote>{dict.last_words_note}</PlayerNote>
          </PlayerCard>

          <ContentSection>
            <MetaInfo style={{  }}>
              <MetaHighlight>{eliminatedPlayerData?.nickname}</MetaHighlight> {dict.final_speech}
            </MetaInfo>

            {isFirstNight && firstDayEliminations >= 2 && (
              <InstructionCard>
                <strong>{dict.best_move_unavailable}</strong> {dict.first_day_eliminations.replace('{0}', String(firstDayEliminations))}
              </InstructionCard>
            )}
          </ContentSection>
        </PlayerSection>

        <ActionButtons>
          <PhaseButton $variant="primary" onClick={handleLastWordsComplete}>
            {dict.rdy}
          </PhaseButton>
          {onUndo && (
            <UndoButton $variant="secondary" onClick={onUndo}>
              {dict.back_to_night}
            </UndoButton>
          )}
        </ActionButtons>
      </TransitionCard>
    );
  }

  return (
    <TransitionCard $variant="warning">
      <TransitionHeader>
        <TransitionTitle>{dict.morning}</TransitionTitle>
        <CloseButton onClick={onContinue}>×</CloseButton>
      </TransitionHeader>

      <PlayerSection>
        <PlayerCard>
          <PlayerAvatarLarge
            src={eliminatedPlayerData?.avatar}
            alt={eliminatedPlayerData?.nickname}
          />
          <PlayerNameLarge>{eliminatedPlayer}. {eliminatedPlayerData?.nickname}</PlayerNameLarge>
        </PlayerCard>

        <ContentSection>
          <MetaInfo style={{  }}>
            <MetaHighlight>{eliminatedPlayerData?.nickname}</MetaHighlight> {dict.eliminated_night}.
          </MetaInfo>
        </ContentSection>
      </PlayerSection>

      <ActionButtons>
        <PhaseButton $variant="primary" onClick={onContinue}>
          {dict.continue_to_day_number.replace('{0}', String(nightCount + 1))}
        </PhaseButton>
        {onUndo && (
          <UndoButton $variant="secondary" onClick={onUndo}>
            {dict.back_to_night}
          </UndoButton>
        )}
      </ActionButtons>
    </TransitionCard>
  );
};

export default MorningTransition;
